import { Alert, Button, Empty, Input, Message, Spin, Typography } from "@arco-design/web-react";
import { Pencil, RotateCcw, Trash2, Users, Wallet } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";

import {
  feeAdjustmentSpecUnit,
  getSafeFulfillmentOrderActionErrorMessage,
  previewStructuredFeeAdjustment,
} from "../../fulfillment-order/service";
import type { FulfillmentOrderLine } from "../../fulfillment-order/types";
import { FulfillmentOrderQuickAdjustmentModal } from "../../fulfillment-order/ui/FulfillmentOrderQuickAdjustmentModal";
import {
  useCompleteFeeAdjustment,
  useFeeAdjustmentEditor,
  usePreviewFeeAdjustment,
  useWorkspaceFeeAdjustmentRequests,
} from "../runtime";
import type { FeeAdjustmentLineDraft, FeeAdjustmentPreview } from "../types";
import { buildFeeAdjustmentDraftDisplay } from "../service";
import { FeeAdjustmentServiceTable } from "./FeeAdjustmentServiceTable";
import "./feeAdjustment.css";

const formatNumber = (value: string | number | null | undefined, fractionDigits = 2) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "—";
  return Number.isInteger(number) ? String(number) : number.toFixed(fractionDigits).replace(/0+$/u, "").replace(/\.$/u, "");
};
const formatMoney = (value: string | number | null | undefined) => `¥${formatNumber(value)}`;
const deltaDirection = (value: string) => Number(value) > 0 ? "increase" : Number(value) < 0 ? "decrease" : "same";

type Props = {
  workspaceId: string;
  sourceLines: FulfillmentOrderLine[];
  canAccess: boolean;
  canUpdate: boolean;
  canFinalize: boolean;
  onActiveRequestTextChange?: (text: string | null) => void;
  adjustingLine?: FulfillmentOrderLine | null;
  onAdjustingLineChange?: (line: FulfillmentOrderLine | null) => void;
  embedded?: boolean;
  groupDescriptionAmounts?: boolean;
  formatAmount?: (value: string | null | undefined, signed?: boolean) => string;
  sourceLinesSlot?: ReactNode;
  activeRequestId?: string | null;
  onActiveRequestChange?: (requestId: string | null) => void;
  onDirtyChange?: (dirty: boolean) => void;
  onPendingChange?: (pending: boolean) => void;
  renderQuickEntry?: (
    onCreated: (lines: FeeAdjustmentLineDraft[]) => void,
    controls: { disabled: boolean; onDirtyChange: (dirty: boolean) => void },
  ) => ReactNode;
};

/**
 *
 */
function uniqueKey(prefix: string): string {
  return `${prefix}-${globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`}`;
}

/** 计调在确认件当前页面处理一笔销售发起的费用调整。 */
export function FeeAdjustmentWorkspacePanel({
  workspaceId,
  sourceLines,
  canAccess,
  canUpdate,
  canFinalize,
  onActiveRequestTextChange,
  adjustingLine: controlledAdjustingLine,
  onAdjustingLineChange,
  embedded = false,
  groupDescriptionAmounts = false,
  formatAmount,
  sourceLinesSlot,
  renderQuickEntry,
  activeRequestId: controlledRequestId,
  onActiveRequestChange,
  onDirtyChange,
  onPendingChange,
}: Props) {
  const hasEmbeddedSource = embedded && sourceLinesSlot !== undefined;
  const requests = useWorkspaceFeeAdjustmentRequests(workspaceId, canAccess && !hasEmbeddedSource);
  const [localRequestId, setLocalRequestId] = useState<string | null>(null);
  const activeRequestId = controlledRequestId === undefined ? localRequestId : controlledRequestId;
  const sessionRef = useRef({ workspaceId, requestId: activeRequestId });
  if (sessionRef.current.workspaceId !== workspaceId || sessionRef.current.requestId !== activeRequestId) {
    sessionRef.current = { workspaceId, requestId: activeRequestId };
  }
  const session = sessionRef.current;
  const mountedRef = useRef(true);
  const savingRef = useRef<typeof session | null>(null);
  const [savingSession, setSavingSession] = useState<typeof session | null>(null);
  const saving = savingSession === session;
  const completionIntent = useRef<{ session: typeof session; fingerprint: string; key: string } | null>(null);
  const currentSession = () => mountedRef.current && sessionRef.current === session;
  const permissionsRef = useRef({ canAccess, canUpdate, canFinalize });
  permissionsRef.current = { canAccess, canUpdate, canFinalize };
  const canMutate = () => currentSession() && savingRef.current !== session
    && permissionsRef.current.canAccess && permissionsRef.current.canUpdate && permissionsRef.current.canFinalize;
  const setActiveRequestId = (requestId: string | null) => {
    if (!currentSession() || savingRef.current === session) return;
    if (controlledRequestId === undefined) setLocalRequestId(requestId);
    onActiveRequestChange?.(requestId);
  };
  const editor = useFeeAdjustmentEditor(canAccess ? activeRequestId : null);
  const previewMutation = usePreviewFeeAdjustment(activeRequestId);
  const completeMutation = useCompleteFeeAdjustment(workspaceId, activeRequestId);
  const [drafts, setDrafts] = useState<FeeAdjustmentLineDraft[]>([]);
  const [draftReasons, setDraftReasons] = useState<Record<string, string>>({});
  const [newServiceDraftId, setNewServiceDraftId] = useState<string | null>(null);
  const [preview, setPreview] = useState<FeeAdjustmentPreview | null>(null);
  const [processingBasis, setProcessingBasis] = useState("");
  const [modalDirty, setModalDirty] = useState(false);
  const [quickEntryDirty, setQuickEntryDirty] = useState(false);
  const draftsRef = useRef<HTMLElement>(null);
  const [localAdjustingLine, setLocalAdjustingLine] = useState<FulfillmentOrderLine | null>(null);
  const adjustingLine = controlledAdjustingLine === undefined ? localAdjustingLine : controlledAdjustingLine;
  const setAdjustingLine = (line: FulfillmentOrderLine | null) => {
    if (!canMutate()) return;
    if (controlledAdjustingLine === undefined) setLocalAdjustingLine(line);
    onAdjustingLineChange?.(line);
  };
  const draftGeneration = useRef(0);
  const reportRef = useRef({ onDirtyChange, onPendingChange });
  reportRef.current = { onDirtyChange, onPendingChange };
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      reportRef.current.onDirtyChange?.(false);
      reportRef.current.onPendingChange?.(false);
    };
  }, []);
  const dirty = Boolean(activeRequestId && (drafts.length || processingBasis.trim() || modalDirty || quickEntryDirty || saving));
  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);
  useEffect(() => { onPendingChange?.(saving); }, [saving, onPendingChange]);
  const reportQuickEntryDirty = useCallback((value: boolean) => {
    if (mountedRef.current && sessionRef.current === session && savingRef.current !== session) setQuickEntryDirty(value);
  }, [session]);

  useEffect(() => {
    draftGeneration.current += 1;
    setDrafts([]);
    setDraftReasons({});
    setNewServiceDraftId(null);
    setPreview(null);
    setProcessingBasis("");
    setModalDirty(false);
    setQuickEntryDirty(false);
    completionIntent.current = null;
  }, [workspaceId, activeRequestId]);

  const candidate = editor.data?.request;
  const active = canAccess && candidate?.request_id === activeRequestId && candidate.fulfillment_workspace_id === workspaceId
    && candidate.status === "pending" ? candidate : undefined;
  useEffect(() => {
    if (active?.request_text) onActiveRequestTextChange?.(active.request_text);
  }, [active?.request_text, onActiveRequestTextChange]);
  const payerCount = active?.members.filter((member) => member.is_payer).length ?? 0;
  const nonPayerCount = (active?.members.length ?? 0) - payerCount;
  const sourceById = useMemo(
    () => new Map(editor.data?.source_lines.map((line) => [line.line_id, line]) ?? []),
    [editor.data?.source_lines],
  );
  const newServiceDraft = drafts.find((draft) => draft.draft_line_id === newServiceDraftId);
  const newServiceLine = useMemo<FulfillmentOrderLine | null>(() => newServiceDraft ? {
    id: newServiceDraft.draft_line_id, workspaceId, fulfillmentOrderId: null, sourceBudgetLineId: null,
    sourceType: "manual", lineNo: 0, resourceType: newServiceDraft.resource_type ?? "",
    itemNameSnapshot: newServiceDraft.item_name ?? "新增费用", itemSpecSnapshot: newServiceDraft.item_spec,
    fulfillmentSupplierName: newServiceDraft.service_provider_name,
    settlementSupplierName: newServiceDraft.settlement_supplier_name,
    pricingUnitType: newServiceDraft.after.pricing_unit_type, quantity: "0", billingCount: newServiceDraft.after.billing_count,
    unit: newServiceDraft.after.unit, unitPriceAmount: newServiceDraft.after.unit_price_amount, amount: "0", status: "active",
  } : null, [newServiceDraft, workspaceId]);
  const adjustingDraft = newServiceDraft ?? drafts.find((draft) => draft.source_confirmation_line_id === adjustingLine?.id);
  const initialAdjustment = useMemo(() => adjustingDraft ? {
    quantity: adjustingDraft.after.quantity,
    billingCount: adjustingDraft.after.billing_count,
    specUnit: adjustingDraft.after.spec_unit,
    unitPriceAmount: adjustingDraft.after.unit_price_amount,
  } : undefined, [adjustingDraft]);

  if (!canAccess && !hasEmbeddedSource) return null;

  /**
   *
   */
  function replaceDraft(next: FeeAdjustmentLineDraft) {
    if (!canMutate()) return;
    completionIntent.current = null;
    draftGeneration.current += 1;
    setDrafts((current) => [
      ...current.filter((line) => line.source_confirmation_line_id !== next.source_confirmation_line_id),
      next,
    ]);
    setPreview(null);
  }

  /** 新增服务只追加本地草稿，并使已计算的预览失效。 */
  function appendDrafts(lines: FeeAdjustmentLineDraft[]) {
    if (!active || !canMutate()) return;
    completionIntent.current = null;
    draftGeneration.current += 1;
    setDrafts((current) => [...current, ...lines]);
    setPreview(null);
  }

  /**
   *
   */
  async function runPreview() {
    if (!active || !canMutate() || drafts.length === 0) return;
    const previewGeneration = draftGeneration.current;
    try {
      const result = await previewMutation.mutateAsync({
        expected_revision: active.revision,
        lines: drafts,
      });
      if (!currentSession() || draftGeneration.current !== previewGeneration) return;
      setPreview(result);
      if (!result.can_complete) Message.error("R3 重算存在阻断项，请先修正调整明细");
    } catch (error) {
      if (!currentSession() || draftGeneration.current !== previewGeneration) return;
      setPreview(null);
      Message.error(getSafeFulfillmentOrderActionErrorMessage(
        error,
        "R3 重算失败，请检查费用明细后重试",
      ));
    }
  }

  /**
   *
   */
  async function save() {
    if (!active || !canMutate() || !preview?.can_complete || !processingBasis.trim() || modalDirty || quickEntryDirty) return;
    const payload = { expected_revision: active.revision, lines: drafts, processing_basis: processingBasis.trim() };
    const fingerprint = JSON.stringify(payload);
    if (completionIntent.current?.session !== session || completionIntent.current.fingerprint !== fingerprint) {
      completionIntent.current = { session, fingerprint, key: uniqueKey("fee-adjustment-complete") };
    }
    savingRef.current = session;
    setSavingSession(session);
    try {
      await completeMutation.mutateAsync({
        ...payload,
        idempotency_key: completionIntent.current.key,
      });
      if (!currentSession()) return;
      completionIntent.current = null;
      savingRef.current = null;
      Message.success("费用调整已保存");
      setActiveRequestId(null);
    } catch {
      if (currentSession()) Message.error("费用调整保存失败，当前内容已保留，请重试");
    } finally {
      if (savingRef.current === session) savingRef.current = null;
      if (currentSession()) setSavingSession(null);
    }
  }

  return (
    <section className={hasEmbeddedSource && !active ? undefined : `fee-adjustment-workspace${embedded ? " fee-adjustment-workspace--embedded" : ""}`} aria-label={hasEmbeddedSource && !active ? undefined : "费用调整"}>
      {!hasEmbeddedSource && !activeRequestId ? (
        <section className="fee-adjustment-pending" aria-label="费用调整待办">
          <div className="fee-adjustment-section-heading">
            <div>
              <Typography.Title heading={5}>费用调整待办</Typography.Title>
              <Typography.Text type="secondary">销售发起后，由计调在当前确认件页面处理。</Typography.Text>
            </div>
            <Button type="text" onClick={() => void requests.refetch()}>刷新</Button>
          </div>
          {requests.isLoading ? <Spin /> : null}
          {requests.isError ? <Alert type="error" content="费用调整待办加载失败" /> : null}
          {!requests.isLoading && !requests.isError && !(requests.data?.length) ? <Empty description="暂无费用调整待办" /> : null}
          <div className="fee-adjustment-request-list">
            {(requests.data ?? []).map((request) => (
              <article key={request.request_id} className="fee-adjustment-request-card">
                <div><strong>{request.request_text}</strong><small>申请编号：{request.request_id}</small></div>
                <span>{request.members.filter((member) => member.is_payer).length} 人付款 · {request.members.filter((member) => !member.is_payer).length} 人不付款</span>
                <Button
                  type="primary"
                  aria-label="编辑"
                  disabled={!canUpdate || !canFinalize || saving}
                  onClick={() => {
                    if (!canMutate()) return;
                    onActiveRequestTextChange?.(request.request_text);
                    setActiveRequestId(request.request_id);
                  }}
                >
                  处理
                </Button>
              </article>
            ))}
          </div>
        </section>
      ) : null}
      {canAccess && activeRequestId && editor.isLoading ? <Spin /> : null}
      {canAccess && activeRequestId && !editor.isLoading && !active ? (
        <div className="fee-adjustment-load-error">
          <Alert type="error" content="费用调整编辑上下文加载失败，请取消后重试" />
          <Button onClick={() => setActiveRequestId(null)}>取消费用调整</Button>
        </div>
      ) : null}
      <section className={active ? "fee-adjustment-editor" : undefined} aria-label={active ? "费用调整编辑区" : undefined}>
        {active ? <>
          {!embedded ? <Alert
            type="warning"
            title={`正在编辑费用调整：${active.request_text}`}
            content={`批次：${active.request_id} · ${payerCount} 人付款 · ${nonPayerCount} 人不付款`}
          /> : null}
          {renderQuickEntry ? (
            <section className="fee-adjustment-new-lines" aria-label="新增费用项目">
              {renderQuickEntry(appendDrafts, { disabled: saving || !canUpdate || !canFinalize, onDirtyChange: reportQuickEntryDirty })}
              {drafts.length > 0 ? <div className="fee-adjustment-section-heading">
                <span role="status">本次调整已加入 {drafts.length} 项（未保存）</span>
                <Button type="text" onClick={() => {
                  draftsRef.current?.scrollIntoView?.({ block: "start" });
                  draftsRef.current?.focus({ preventScroll: true });
                }}>查看本次调整明细</Button>
              </div> : null}
            </section>
          ) : null}
        </> : null}

        {sourceLinesSlot}

        {active ? <>
          {!embedded ? <div className="fee-adjustment-source-list" aria-label="确认件费用列表">
            <div className="fee-adjustment-section-heading">
              <Typography.Title heading={5}>确认件费用列表</Typography.Title>
              <Typography.Text type="secondary">点击调整后加入本次费用调整草稿。</Typography.Text>
            </div>
            {sourceLines.filter((line) => line.status === "active").map((line) => (
              <article key={line.id} className="fee-adjustment-source-row">
                <div><strong>#{line.lineNo} {line.itemNameSnapshot}</strong><small>{line.resourceType}</small></div>
                <span>{line.quantity} {line.unit} × {line.billingCount} {feeAdjustmentSpecUnit(line.resourceType)} × ¥{line.unitPriceAmount} = ¥{line.amount}</span>
                <Button disabled={!line.settlementSupplierId || !canUpdate || !canFinalize || saving} title={!line.settlementSupplierId ? "请先设置结算主体" : undefined} onClick={() => setAdjustingLine(line)}>调整</Button>
              </article>
            ))}
          </div> : null}

          <section ref={draftsRef} tabIndex={-1} className="fee-adjustment-drafts" aria-label="本次费用调整明细">
            <Typography.Title heading={5}>本次费用调整明细</Typography.Title>
            {drafts.length === 0 ? <Empty description="尚未添加调整明细" /> : <FeeAdjustmentServiceTable
              label="本次费用调整明细" amountKind="cost"
              groupDescriptionAmounts={groupDescriptionAmounts}
              formatAmount={formatAmount}
              lines={drafts.map((draft) => buildFeeAdjustmentDraftDisplay(draft, sourceLines.find((line) => line.id === draft.source_confirmation_line_id)))}
              renderActions={(display) => {
              const draft = drafts.find((item) => item.draft_line_id === display.id)!;
              const sourceLine = sourceLines.find((line) => line.id === draft.source_confirmation_line_id);
              const itemName = display.item_name;
              return (
                  <div className="fee-adjustment-draft-actions">
                    <Button
                      type="text"
                      size="small"
                      icon={<Pencil size={15} />}
                      aria-label={`编辑 ${itemName}`}
                      disabled={(draft.adjustment_type === "modify_existing" && !sourceLine) || !canUpdate || !canFinalize || saving}
                      onClick={() => { if (canMutate()) { if (draft.adjustment_type === "add_service") setNewServiceDraftId(draft.draft_line_id); else if (sourceLine) setAdjustingLine(sourceLine); } }}
                    >
                      编辑
                    </Button>
                  <Button
                    type="text"
                    size="small"
                    status="danger"
                    icon={<Trash2 size={15} />}
                    disabled={!canUpdate || !canFinalize || saving}
                    onClick={() => {
                      if (!canMutate()) return;
                      completionIntent.current = null;
                      draftGeneration.current += 1;
                      setDrafts((current) => current.filter((line) => line.draft_line_id !== draft.draft_line_id));
                      setPreview(null);
                    }}
                  >
                    删除
                  </Button>
                  </div>
              );
            }} />}
          </section>

          <section className="fee-adjustment-r3" aria-label="R3 试算">
            <div className="fee-adjustment-section-heading">
              <Typography.Title heading={5}>R3 试算</Typography.Title>
              <Button
                type="outline"
                icon={<RotateCcw size={15} />}
                loading={previewMutation.isPending}
                disabled={drafts.length === 0 || saving || !canUpdate || !canFinalize}
                onClick={() => void runPreview()}
              >
                R3 重算
              </Button>
            </div>
            {preview ? (
              <>
                <div className="fee-adjustment-r3-result" aria-live="polite">
                  <article className="fee-adjustment-metric fee-adjustment-metric--cost" aria-label="本次成本变化">
                    <div className="fee-adjustment-metric-heading"><span>本次成本变化</span><Wallet aria-hidden="true" /></div>
                    <strong className="fee-adjustment-metric-amount" data-direction={deltaDirection(preview.cost_delta_amount)}>{formatAmount ? formatAmount(preview.cost_delta_amount) : formatMoney(preview.cost_delta_amount)}</strong>
                  </article>
                  <article className="fee-adjustment-metric fee-adjustment-metric--receivable" aria-label="人员应收变化">
                    <div className="fee-adjustment-metric-heading"><span>人员应收变化</span><Users aria-hidden="true" /></div>
                    <strong className="fee-adjustment-metric-amount" data-direction={deltaDirection(preview.receivable_delta_amount)}>{formatAmount ? formatAmount(preview.receivable_delta_amount) : formatMoney(preview.receivable_delta_amount)}</strong>
                  </article>
                </div>
                {preview.errors.map((error) => <Alert key={error.code + error.draft_line_id} type={error.blocking ? "error" : "warning"} content={error.message} />)}
              </>
            ) : <Typography.Text type="secondary">尚未进行 R3 重算</Typography.Text>}
          </section>
          <label className="fee-adjustment-processing-basis">
            <span className="fee-adjustment-field-title">处理依据</span>
            <Input.TextArea value={processingBasis} aria-label="处理依据" disabled={saving || !canUpdate || !canFinalize} onChange={(value) => {
              if (!canMutate()) return;
              if (value.trim() !== processingBasis.trim()) completionIntent.current = null;
              setProcessingBasis(value);
            }} />
          </label>
          <div className="fee-adjustment-actions">
            <Button disabled={saving} onClick={() => setActiveRequestId(null)}>取消</Button>
            <Button
              type="primary"
              loading={saving}
              disabled={!canUpdate || !canFinalize || saving || !preview?.can_complete || !processingBasis.trim() || modalDirty || quickEntryDirty}
              onClick={() => void save()}
            >
              保存费用调整
            </Button>
          </div>
        </> : null}
      </section>

      {canAccess ? <FulfillmentOrderQuickAdjustmentModal
        formatAmount={formatAmount}
        key={`${workspaceId}:${activeRequestId}`}
        line={newServiceLine ?? adjustingLine}
        mode="fee-adjustment"
        isNewService={Boolean(newServiceDraft)}
        initialAdjustment={initialAdjustment}
        initialReason={adjustingDraft ? draftReasons[adjustingDraft.draft_line_id] : undefined}
        submitting={saving}
        onDirtyChange={setModalDirty}
        onClose={() => { if (canMutate()) { setNewServiceDraftId(null); setAdjustingLine(null); } }}
        onSubmit={async (line, adjustment, reason) => {
          if (!active || !canMutate()) throw new Error("当前申请正在提交或已切换，请稍后重试");
          const amountPreview = previewStructuredFeeAdjustment(line, adjustment);
          if (!amountPreview.normalized || amountPreview.error) throw new Error(amountPreview.error);
          if (newServiceDraft) {
            completionIntent.current = null;
            draftGeneration.current += 1;
            setDrafts((current) => current.map((draft) => draft.draft_line_id === newServiceDraft.draft_line_id ? {
              ...draft, after: { ...draft.after, quantity: amountPreview.normalized!.quantity,
                billing_count: amountPreview.normalized!.billingCount, spec_unit: amountPreview.normalized!.specUnit,
                unit_price_amount: amountPreview.normalized!.unitPriceAmount, amount: amountPreview.amount },
            } : draft));
            setDraftReasons((current) => ({ ...current, [newServiceDraft.draft_line_id]: reason }));
            setPreview(null);
            return;
          }
          const source = sourceById.get(line.id);
          if (!source) throw new Error("费用调整来源确认行已变化");
          const draftLineId = adjustingDraft?.draft_line_id ?? uniqueKey("fee-adjustment-line");
          setDraftReasons((current) => ({ ...current, [draftLineId]: reason }));
          replaceDraft({
            draft_line_id: draftLineId,
            adjustment_type: "modify_existing",
            source_confirmation_line_id: line.id,
            item_name: null,
            resource_type: null,
            service_provider_type: null,
            service_provider_id: null,
            settlement_supplier_id: null,
            expected_before: adjustingDraft?.expected_before ?? source.current,
            after: {
              ...source.current,
              quantity: amountPreview.normalized.quantity,
              billing_count: amountPreview.normalized.billingCount,
              spec_unit: amountPreview.normalized.specUnit,
              unit_price_amount: amountPreview.normalized.unitPriceAmount,
              amount: amountPreview.amount,
            },
          });
        }}
      /> : null}
    </section>
  );
}
